import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { 
  Briefcase, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Calendar,
  Search,
  ArrowRight,
  Sparkles,
  FileText,
  User,
  Hourglass,
  XCircle,
  MapPin,
  Users,
  DollarSign,
  Building2,
  GraduationCap
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerDashboard() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isPendingApproval = !user?.is_approved;

  useEffect(() => {
    if (!token) return;
    
    const fetchData = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        // Fetch applications first
        const applicationsRes = await axios.get(`${API}/my-applications`, { headers });
        setApplications(applicationsRes.data || []);
      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
      }
      
      try {
        const [statsRes, assignmentsRes] = await Promise.all([
          axios.get(`${API}/dashboard/worker`, { headers }),
          axios.get(`${API}/assignments`, { headers }),
        ]);
        setStats(statsRes.data);
        setAssignments(Array.isArray(assignmentsRes.data) ? assignmentsRes.data.slice(0, 5) : []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const getStatusBadge = (status) => {
    switch(status) {
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Hourglass className="w-3 h-3 mr-1" />{t('common.pending')}</Badge>;
      case "accepted":
      case "selected":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />{t('common.approved')}</Badge>;
      case "rejected":
      case "not_selected":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />{t('common.rejected')}</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { style: 'decimal', minimumFractionDigits: 0 }).format(amount || 0) + ' kr';
  };

  // Calculate application stats
  const pendingApps = (applications || []).filter(a => a.status === "pending").length;
  const acceptedApps = (applications || []).filter(a => a.status === "accepted" || a.status === "selected").length;

  if (loading) {
    return (
      <DashboardLayout navItems={workerNavItems} title={t('nav.dashboard')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={workerNavItems} title={t('nav.dashboard')}>
      <div className="space-y-8" data-testid="worker-dashboard">
        {/* Pending Approval Banner */}
        {isPendingApproval && (
          <div className="relative overflow-hidden rounded-xl border border-[#FF5F1F]/30 bg-gradient-to-r from-[#FF5F1F]/10 via-[#FF5F1F]/5 to-transparent p-6">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF5F1F]/10 rounded-full blur-3xl"></div>
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#FF5F1F]/20 rounded-lg">
                  <Clock className="w-6 h-6 text-[#FF5F1F]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{t('common.pending')}</h3>
                  <p className="text-sm text-[#94A3B8]">
                    {t('profile.profileIncomplete')}
                  </p>
                </div>
              </div>
              <Link to="/worker/profile">
                <Button variant="outline" className="border-[#FF5F1F]/50 text-[#FF5F1F] hover:bg-[#FF5F1F]/10 whitespace-nowrap">
                  <User className="w-4 h-4 mr-2" />
                  {t('profile.editProfile')}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Browse Projects - Animated & Prominent */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#23F7DD] via-[#4F46E5] to-[#23F7DD] rounded-xl blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
          <Link to="/worker/browse-projects" className="block">
            <Card className="relative bg-gradient-to-br from-[#0A0A0A] to-[#111] border-0 overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#23F7DD] rounded-lg blur-md opacity-50 animate-ping"></div>
                      <div className="relative p-4 bg-[#23F7DD] rounded-lg">
                        <Search className="w-8 h-8 text-[#050505]" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-['Unbounded'] font-bold text-white">{t('projects.searchProjects')}</h3>
                        <Sparkles className="w-5 h-5 text-[#23F7DD] animate-pulse" />
                      </div>
                      <p className="text-[#94A3B8]">
                        {isPendingApproval 
                          ? t('projects.viewProject') 
                          : t('projects.applyToProject')}
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2 text-[#23F7DD] font-semibold">
                    <span>{t('common.view')}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Welcome */}
        <div>
          <h2 className="text-xl font-['Unbounded'] font-semibold text-white mb-1">
            {t('dashboard.welcome', { name: user?.name })}
          </h2>
          <p className="text-[#94A3B8]">
            {isPendingApproval ? t('profile.editProfile') : t('dashboard.overview')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg">
                  <Briefcase className="w-6 h-6 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                    {stats?.total_assignments || 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('nav.assignments')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Hourglass className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-yellow-500">
                    {pendingApps}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('applications.pendingApplications')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {acceptedApps}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('applications.approvedApplications')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-purple-500">
                    {(assignments || []).filter(a => a.status === 'pending' || a.status === 'accepted').length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">{t('dashboard.upcomingTasks')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Applications Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              {t('applications.myApplications')} ({applications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-[#94A3B8]">{t('applications.noApplications')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('workerApplications.noApplicationsDescription')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.slice(0, 5).map((app) => {
                  const project = app.project || {};
                  const projectTitle = project.title || app.project_title || `${t('projects.title')} #${app.project_id?.slice(-8)}`;
                  const isApproved = app.status === 'accepted' || app.status === 'selected';
                  
                  return (
                    <div
                      key={app.application_id}
                      className={`p-4 rounded-lg border transition-all ${
                        isApproved
                          ? 'bg-green-500/5 border-green-500/30' 
                          : app.status === 'rejected' || app.status === 'not_selected'
                          ? 'bg-red-500/5 border-red-500/30'
                          : 'bg-[#111] border-white/10 hover:border-[#23F7DD]/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-white font-semibold">{projectTitle}</h4>
                            {getStatusBadge(app.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                            {project.location_city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {project.location_city}
                              </span>
                            )}
                            {project.start_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(project.start_date).toLocaleDateString('nb-NO')}
                              </span>
                            )}
                            {(project.net_to_team || app.net_to_team) && (
                              <span className="flex items-center gap-1 text-[#23F7DD]" title="Brutto lønn (før skatt)">
                                <DollarSign className="w-4 h-4" />
                                {(project.net_to_team || app.net_to_team)?.toLocaleString()} kr brutto
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-sm text-gray-500">
                            {app.created_at && new Date(app.created_at).toLocaleDateString('nb-NO')}
                          </span>
                          {isApproved && (
                            <Link 
                              to={`/worker/my-projects`}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-colors"
                            >
                              {t('common.view')}
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {applications.length > 5 && (
                  <Link to="/worker/applications" className="block text-center text-[#23F7DD] hover:underline py-2">
                    {t('dashboard.viewAll')} {applications.length} {t('applications.applications').toLowerCase()} →
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Assignments */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-['Unbounded']">{t('dashboard.upcomingTasks')}</CardTitle>
            <Link to="/worker/assignments" className="text-[#23F7DD] hover:underline text-sm">
              {t('dashboard.viewAll')}
            </Link>
          </CardHeader>
          <CardContent>
            {(assignments || []).length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('common.noData')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(assignments || []).map((assignment) => (
                  <div
                    key={assignment.assignment_id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg"
                  >
                    <div>
                      <p className="text-white font-medium">{t('projects.title')} #{assignment.project_id?.slice(-8)}</p>
                      <p className="text-sm text-[#94A3B8]">{assignment.scheduled_date}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        assignment.status === "completed"
                          ? "bg-green-500/20 text-green-400 border-green-500/30"
                          : assignment.status === "in_progress"
                          ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                          : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                      }`}
                    >
                      {assignment.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coming Soon - Courses Card */}
      <Card className="bg-gradient-to-br from-[#10b981]/10 to-[#10b981]/5 border-[#10b981]/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#10b981]/20 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-[#10b981]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Stillaskurs</h3>
                <p className="text-sm text-[#94A3B8]">Profesjonell opplæring og sertifisering</p>
              </div>
            </div>
            <Badge className="bg-[#10b981]/30 text-[#10b981] border-[#10b981]/50 px-3 py-1 text-xs font-bold animate-pulse">
              KOMMER SNART
            </Badge>
          </div>
          <p className="text-[#94A3B8] text-sm mb-4">
            Snart kan du ta stillaskurs direkte på plattformen! Lær gjennom online leksjoner, videoopplæring, og ta sertifiseringseksamen.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-xs">
              📚 Online Leksjoner
            </span>
            <span className="px-3 py-1.5 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-xs">
              🎬 Video Opplæring
            </span>
            <span className="px-3 py-1.5 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-xs">
              📝 Quiz & Eksamen
            </span>
            <span className="px-3 py-1.5 bg-[#10b981]/10 border border-[#10b981]/30 rounded-full text-[#10b981] text-xs">
              🏆 Sertifikat
            </span>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
